import { notEmpty } from "@product/scmp-sdk";
import capitalize from "lodash/capitalize";
import first from "lodash/first";
import { useMemo } from "react";
import { graphql, readInlineData } from "react-relay";

import { checkIsStyleArticle } from "~/components/article/article-render/style-article/helper";
import { useContentHelper } from "~/components/content/hooks";
import { section as sectionData } from "~/data/section";
import type { hooksContentHeadlineTagContent$key } from "~/queries/__generated__/hooksContentHeadlineTagContent.graphql";

type Type = "column" | "flag" | "opinion" | "section" | "specialCase";

type HeadlineTagData = {
  color?: string;
  description?: string;
  entityId?: string;
  name?: string;
};

export const useDetermineHeadlineTag = (
  reference: hooksContentHeadlineTagContent$key,
  useSectionTags = false,
): HeadlineTagData | null => {
  const content = readInlineData(
    graphql`
      fragment hooksContentHeadlineTagContent on Content @inline {
        ... on Article {
          types {
            value {
              name
              entityId
              description {
                text
              }
            }
          }
          ...helpersCheckIsStyleArticle
        }
        flag
        sections {
          value {
            name
          }
        }
        ...hooksContentHelperContent
      }
    `,
    reference,
  );

  const { isContentWithFlag, isEditorialOpinionType, isScmpOpinionType, isSpecialArticleType } =
    useContentHelper(content);

  const headlineTagType = useMemo<Type | null>(() => {
    if (isScmpOpinionType) return "opinion";
    else if (isEditorialOpinionType) return "column";
    else if (isContentWithFlag) return "flag";
    else if (isSpecialArticleType) return "specialCase";
    else if (useSectionTags) return "section";
    else return null;
  }, [
    isContentWithFlag,
    isEditorialOpinionType,
    isScmpOpinionType,
    isSpecialArticleType,
    useSectionTags,
  ]);

  if (!notEmpty(headlineTagType)) return null;

  const [firstType, secondType] = (content.types ?? [])
    .flatMap(type => type?.value?.filter(item => notEmpty(item?.entityId)))
    .map(type => ({
      description: type?.description?.text ?? "",
      entityId: type?.entityId ?? "",
      name: type?.name ?? "",
    }));

  switch (headlineTagType) {
    case "column":
      return {
        color: "#34b8b5",
        description: secondType?.description ?? firstType?.description ?? "",
        entityId: secondType?.entityId ?? firstType?.entityId ?? "",
        name: secondType?.name ?? firstType?.name ?? "",
      };
    case "flag":
      return {
        color: "#c79a00",
        name: capitalize(content?.flag ?? ""),
      };
    case "opinion":
      return {
        color: checkIsStyleArticle(content) ? "#222222" : "#34b8b5",
        description: firstType?.description ?? "",
        entityId: firstType?.entityId ?? "",
        name: "Opinion",
      };
    case "section":
      return {
        color: "#666666",
        name: first(
          content.sections
            ?.flatMap(section => section?.value)
            ?.map(section => section?.name)
            ?.filter(notEmpty)
            ?.filter(
              section =>
                section !== sectionData.news.name && section !== sectionData.postMagazine.name,
            ) ?? [],
        ),
      };
    case "specialCase":
      return {
        color: "#c79a00",
        description: firstType?.description ?? "",
        entityId: firstType?.entityId ?? "",
        name: firstType?.name ?? "",
      };
    default:
      return null;
  }
};
